import {
  Route,
  createBrowserRouter,
  createRoutesFromElements,
  Navigate,
} from "react-router";

import { Account } from "@/Account";
import { ActionsRoutes, IngestionRulesRoutes } from "@/Actions";
import { Invocation } from "@/Actions/Invocation";
import { Admin } from "@/Admin";
import { AgentsRoutes } from "@/Agents";
import { BoardsRoutes } from "@/Boards";
import { CollectionsRoutes } from "@/Collections";
import { CommentsRoute } from "@/Comments";
import { Dashboard } from "@/Dashboard";
import { DatasetsRoutes } from "@/Datasets";
import { DeviceConfigurationsRedirectRoute, DevicesRoutes } from "@/Device";
import { DeviceTypesRoutes } from "@/DeviceTypes";
import { EventsRoutes } from "@/Events";
import { FilesRoutes } from "@/Files";
import { IntegrationsRoutes } from "@/Integrations";
import { SlackOAuthCallback } from "@/Integrations/SlackIntegration/SlackOAuthCallback";
import { SlackOAuthCallbackError } from "@/Integrations/SlackIntegration/SlackOAuthCallbackError";
import * as slackOAuthCallback from "@/Integrations/SlackIntegration/slackOAuthCallbackLoader";
import { Invite } from "@/Invite";
import { LinkSlack } from "@/LinkSlack";
import { MetricsDashboardPage } from "@/MetricsDashboard";
import { Org } from "@/Org";
import { queryClient } from "@/providers/QueryClient";
import { VisualizationStateProvider } from "@/providers/VisualizationState";
import { Search } from "@/Search";
import { Secrets } from "@/Secrets";
import { SessionsRoutes } from "@/Sessions";
import { OrganizationAssetsRedirectRoute, Settings } from "@/Settings";
import { LoadingScreen } from "@/shared/components";
import {
  Workspace,
  WorkspaceErrorBoundary,
} from "@/shared/components/visualization";
import * as visualizationData from "@/shared/components/visualization/loader/loader";
import { WorkspaceSidebarProvider } from "@/shared/components/visualization/sidebarContext";
import { Env } from "@/shared/environment";
import { Goto, metricsWidgetRoutePatterns } from "@/shared/navigation/Goto";
import {
  featureFlagRoute,
  makeFeatureFlagRedirectLoader,
} from "@/shared/routing/featureFlagRoute";
import { CreateTrigger, EditTrigger, ListTriggers, Trigger } from "@/Triggers";
import { User } from "@/User";

import { AppLayout } from "./AppLayout";
import { AppProvidersLayout } from "./AppProvidersLayout";
import { AuthenticationLayout } from "./AuthenticationLayout";
import { AuthenticationRoutes } from "./AuthenticationRoutes";
import { ErrorPage } from "./ErrorPage";
import { NotFoundPage } from "./NotFoundPage";
import { RobotoUriRedirect } from "./RobotoUriRedirect";
import { ScrollRestorationLayout } from "./ScrollRestorationLayout";
import { UnauthorizedPage } from "./UnauthorizedPage";

export const router = createBrowserRouter(
  createRoutesFromElements(
    // `hydrateFallbackElement` is where v7 moved RouterProvider's `fallbackElement`:
    // without it a hard navigation to a route with a loader shows a blank document
    // until that loader settles. On the root so it covers every branch, which is the
    // scope `fallbackElement` had.
    <Route
      element={<AppProvidersLayout />}
      errorElement={<ErrorPage />}
      hydrateFallbackElement={<LoadingScreen />}
    >
      <Route element={<ScrollRestorationLayout />}>
        {/** Sign-in, sign-up, and credential approval routes */}
        {...AuthenticationRoutes()}

        {/** User authenticated routes */}
        <Route element={<AuthenticationLayout isNotOrgProtected />}>
          <Route path="/invites/:inviteId" element={<Invite />} />
        </Route>

        {/** User and org authenticated routes */}
        <Route element={<AuthenticationLayout />}>
          {/* Org-protected, unlike /invites above: accepting an invite is how someone becomes
              org-ful, but a Slack link is not. Every Slack turn is gated on org membership, so
              a link claimed by an org-less account can reach nothing. Registered, like the
              callback below, only where the deployment serves Slack. */}
          {Env.slackEnabled && (
            <Route
              path={`${Goto.route.LinkSlack}/:slackLinkNonce`}
              element={<LinkSlack />}
            />
          )}
          {/* Slack's OAuth redirect lands here rather than among the /integrations children
              below, because it has to sit outside AppLayout (like /invites/:inviteId above):
              this renders one line of status in a 600x800 popup that closes on its first
              effect, and the sidebar's own queries would fire in a document about to be
              destroyed. AuthenticationLayout gates only what renders: loaders run before any
              element in their branch mounts, so whatever precondition the exchange needs has
              to live in the loader. */}
          {Env.slackEnabled && (
            <Route
              path={Goto.route.SlackIntegrationCallback}
              element={<SlackOAuthCallback />}
              loader={slackOAuthCallback.slackOAuthCallbackLoader(queryClient)}
              errorElement={<SlackOAuthCallbackError />}
              shouldRevalidate={slackOAuthCallback.shouldRevalidate}
            />
          )}
          <Route element={<AppLayout />}>
            <Route index element={<Dashboard />} />
            <Route path="/account" element={<Account />} />
            {...ActionsRoutes()}
            <Route path="/admin" element={<Admin />} />
            <Route path="/agents">{...AgentsRoutes()}</Route>
            <Route
              path="/boards"
              loader={makeFeatureFlagRedirectLoader(queryClient, {
                flagName: "release-boards",
                redirectTo: "/",
              })}
            >
              {...BoardsRoutes()}
            </Route>
            <Route path="/collections">{...CollectionsRoutes()}</Route>
            <Route path="/comments" element={<CommentsRoute />} />
            {/* Datasets and devices spread their <Route> children directly into the data router
                (rather than rendering a descendant <Routes> element) so react-router registers
                their per-route `loader` functions. Loaders only run for routes in the data-router
                config; a nested <Routes> swallows them silently. */}
            <Route path="/datasets">{...DatasetsRoutes()}</Route>
            <Route path="/devices">{...DevicesRoutes()}</Route>
            <Route path="/device-types">{...DeviceTypesRoutes()}</Route>
            {DeviceConfigurationsRedirectRoute()}
            {OrganizationAssetsRedirectRoute()}
            <Route path="/events">{...EventsRoutes()}</Route>
            {...SessionsRoutes()}
            <Route path="/files">{...FilesRoutes()}</Route>
            <Route
              path="/hub"
              element={<Navigate to="/actions/hub" replace />}
            />
            {...IngestionRulesRoutes()}
            <Route path="/integrations">{...IntegrationsRoutes()}</Route>
            <Route
              path="/invocations/:invocationId/*"
              element={<Invocation />}
            />
            <Route path="/orgs/:orgId/*" element={<Org />} />
            {/* The optional `:dashboardId?` segment carries the active saved
                dashboard; bare /metrics is the "no dashboard selected" view.
                One route (not two) so selecting a dashboard never remounts
                MetricsDashboard mid-session. */}
            {featureFlagRoute({
              path: "/metrics/:dashboardId?",
              flagName: "release-metrics",
              redirectTo: "/",
              element: <MetricsDashboardPage />,
            })}
            {/* The widget editor renders the SAME page element rather than a
                separate page, so MetricsDashboard stays mounted and keeps
                owning the layout, the draft, and the save. */}
            {Object.values(
              metricsWidgetRoutePatterns({ deviceScoped: false }),
            ).map((path) =>
              featureFlagRoute({
                path,
                flagName: "release-metrics",
                redirectTo: "/",
                element: <MetricsDashboardPage />,
              }),
            )}
            <Route path="/open" element={<RobotoUriRedirect />} />
            <Route path="/search" element={<Search />} />
            <Route path="/settings/*" element={<Settings />} />
            <Route path="/secrets/:secretName" element={<Secrets />} />
            <Route
              path="/topics"
              element={<Navigate to="/search?target=topics" replace />}
            />
            {/* One list shows every trigger; whether it fires on events or a
                schedule is a filter on it, and the old Scheduled tab's URL
                redirects to the list so saved links still land. React Router
                ranks the static `scheduled` and `create` segments above
                `:triggerName`, so neither is mistaken for a trigger name, and
                ranks `:triggerName/edit` above the detail page's splat, so
                editing is its own page rather than a tab. */}
            <Route path="/triggers" element={<ListTriggers />} />
            <Route
              path="/triggers/scheduled"
              element={<Navigate to={Goto.route.Triggers} replace />}
            />
            <Route path="/triggers/create" element={<CreateTrigger />} />
            <Route
              path="/triggers/:triggerName/edit"
              element={<EditTrigger />}
            />
            <Route path="/triggers/:triggerName/*" element={<Trigger />} />
            <Route path="/users/:userId/*" element={<User />} />
            <Route
              path="/visualize/:workspaceId?"
              element={
                <VisualizationStateProvider>
                  <WorkspaceSidebarProvider>
                    <Workspace />
                  </WorkspaceSidebarProvider>
                </VisualizationStateProvider>
              }
              loader={visualizationData.loader(queryClient)}
              errorElement={<WorkspaceErrorBoundary />}
              shouldRevalidate={visualizationData.shouldRevalidate}
            />
          </Route>
        </Route>

        {/** Unauthorized route for 401 errors */}
        <Route path="/unauthorized" element={<UnauthorizedPage />} />

        {/** Catch-all route for 404 errors */}
        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Route>,
  ),
  {
    basename: Env.devBranchName ? `/${Env.devBranchName}` : undefined,
  },
);
